import { useState } from 'react';
import type { AdminFeaturesResponse, FeatureTier } from '@boardflight/shared';
import { FeatureTier as FeatureTiers } from '@boardflight/shared';
import { api, put } from '../api.js';
import { Async, Panel, Time, useAsync } from '../ui.js';

/**
 * Every feature, and where it is in its release (§2).
 *
 * A feature is released by moving it along one line: `off` reaches nobody, `alpha` and `beta` a
 * person at least that far in, `paid` a team on the paid plan and `free` everybody. That is the tier
 * select on each row, which writes `feature_tiers`. A key added to the registry has no row and is
 * `off` until somebody moves it here. It is the one control in this console that changes what
 * **everybody** gets rather than what one account or one team gets, so it says who the new tier
 * reaches and asks first.
 *
 * The counts say who a feature in each tier would reach, which is the question to ask before moving
 * one.
 *
 * ## Every row says what having it does
 *
 * The `effect` column is the point of this screen for anybody who is not the person who wrote the
 * feature. Almost every key is enforced at a narrower point than its name suggests — `analytics`
 * off does not stop a channel that is already collecting, `tokens.api` off does not revoke a
 * token, `assets.library` off does not close the library — and an operator working from the key
 * alone would take away something they did not mean to, or fail to take away the thing they did.
 * The prose comes from `FEATURES` in the shared package, written beside the `requireFeature` calls
 * it describes.
 */
export function Features() {
  const state = useAsync(() => api<AdminFeaturesResponse>('/v1/admin/features'), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function move(key: string, from: FeatureTier, to: FeatureTier, counts: AdminFeaturesResponse['counts']) {
    setError(null);
    if (!window.confirm(`Move "${key}" from ${from} to ${to}? It will reach ${reach(to, counts)}.`)) {
      state.reload();
      return;
    }
    setBusy(key);
    try {
      await put(`/v1/admin/features/${encodeURIComponent(key)}`, { tier: to });
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(null);
      state.reload();
    }
  }

  const [shown, setShown] = useState<'' | FeatureTier>('');

  return (
    <Async state={state}>
      {(data) => {
        const byTier = new Map<FeatureTier, number>();
        for (const row of data.items) byTier.set(row.tier, (byTier.get(row.tier) ?? 0) + 1);
        // In release order, and in a tier by when it last moved: the newest move is the one to check.
        const rows = data.items
          .filter((row) => shown === '' || row.tier === shown)
          .sort(
            (a, b) =>
              FeatureTiers.options.indexOf(a.tier) - FeatureTiers.options.indexOf(b.tier) ||
              (b.updatedAt ?? 0) - (a.updatedAt ?? 0) ||
              a.key.localeCompare(b.key),
          );
        return (
          <>
            {/* Who each tier reaches. A beta feature reaches the alpha too, so beta's tile adds them. */}
            <section className="panel">
              <div className="stats">
                <div className="stat">
                  <span className="stat-label">Alpha reaches</span>
                  <span className="stat-value"><a href="#accounts?tier=alpha">{data.counts.alpha}</a></span>
                  <span className="muted small">accounts</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Beta reaches</span>
                  <span className="stat-value">{data.counts.beta + data.counts.alpha}</span>
                  <span className="muted small"><a href="#accounts?tier=beta">{data.counts.beta} beta</a> + {data.counts.alpha} alpha</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Paid reaches</span>
                  <span className="stat-value">{data.counts.paidTeams}</span>
                  <span className="muted small">paid teams</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Free reaches</span>
                  <span className="stat-value small">everybody</span>
                </div>
              </div>
            </section>

            <Panel
              title="Features"
              actions={
                <div className="tri" role="group" aria-label="Tier">
                  {(['', ...FeatureTiers.options] as const).map((tier) => (
                    <button key={tier || 'all'} className={shown === tier ? 'tri-on' : undefined} onClick={() => setShown(tier)}>
                      {tier || 'All'} <span className="muted">{tier ? (byTier.get(tier) ?? 0) : data.items.length}</span>
                    </button>
                  ))}
                </div>
              }
            >
              {error ? <p className="error">{error}</p> : null}
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Tier</th>
                      <th>Moved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.key}>
                        <td className="prose">
                          <div><span className="mono">{row.key}</span> <span className="muted">· {row.summary}</span></div>
                          {/* What having it does: the one thing an operator must read before moving
                              it, and the longest — so it opens on demand rather than filling the page. */}
                          <details className="effect">
                            <summary className="small">What having it does</summary>
                            <p className="muted small">{row.effect}</p>
                          </details>
                        </td>
                        <td>
                          <select
                            aria-label={`Tier of ${row.key}`}
                            className={`tier tier-${row.tier}`}
                            value={row.tier}
                            disabled={busy !== null}
                            onChange={(e) => void move(row.key, row.tier, e.target.value as FeatureTier, data.counts)}
                          >
                            {FeatureTiers.options.map((tier) => (
                              <option key={tier} value={tier}>
                                {tier}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="muted small nowrap"><Time at={row.updatedAt} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        );
      }}
    </Async>
  );
}

/** Who a feature at `tier` reaches, in the words the confirmation uses. */
function reach(tier: FeatureTier, counts: AdminFeaturesResponse['counts']): string {
  switch (tier) {
    case 'off':
      return 'nobody';
    case 'alpha':
      return `the alpha, ${count(counts.alpha, 'account')}`;
    case 'beta':
      return `the beta and the alpha, ${count(counts.beta + counts.alpha, 'account')}`;
    case 'paid':
      return `every member of a paid team, ${count(counts.paidTeams, 'team')}`;
    case 'free':
      return 'everybody';
  }
}

/** `1 team`, `3 accounts`. */
function count(n: number, noun: string): string {
  return `${n} ${n === 1 ? noun : `${noun}s`}`;
}
